import { isEqual, omit } from 'lodash';

import { getAvailableFilterCriteria } from '../declarations/filter/filter';
import {
    banners,
    campaigns,
    ClientInfo,
    clients,
    DateRangePreset,
    feedFilters,
    groups,
    phrases,
    retargetings,
    usersProfile
} from '../typings/common';
import { CampaignsCalcTypeFilter } from '../typings/domain';
import { FilterPartialExpression } from '../typings/filter';
import { Goal } from '../typings/goals';
import { GridAlertMessageType, GridContextType, GridSourceParams, GridViewType, SortParams } from '../typings/grid';
import { ClientFeatures } from '../typings/state';

import {
    flattenContainer,
    getFilterCriteriaContainersByView,
    getValidExpressions,
    parseFilterFromString,
    preparePeriodFilterAfterLocalStorage,
    serializeFilterToString,
    serializePeriodFilterForSourceParams
} from './FilterHelpers/FilterHelpers';
import { Session } from './Session/Session';
import { UrlHelpers } from './UrlHelpers';

export interface NormalizeSourceParamsData {
    viewString?: string;
    campaignsIds?: Array<string>;
    groupsIds?: Array<string>;
    goalIds?: Array<string>;
    sortKey?: string;
    sortDirection?: string;
    sortGoalId?: string;
    statPreset?: string;
    statFrom?: string;
    statTo?: string;
    filter?: string;
    login: string;
    messageType: string;
    alertMessageFilter?: string;
    hasOnlyCpmCampaigns?: boolean;
    campaignsCalcTypeFilter: CampaignsCalcTypeFilter;
    clientInfo?: ClientInfo;
}

export class GridSourceHelper {
    static normalizeSourceParamsWithoutFilters(viewString: string, login: string, pathname: string): GridSourceParams {
        let context: GridContextType;
        let view: GridViewType;

        switch (viewString) {
            case campaigns:
                context = clients;
                view = campaigns;
                break;
            case groups:
                context = campaigns;
                view = groups;
                break;
            case banners:
                context = groups;
                view = banners;
                break;
            case phrases:
                context = groups;
                view = phrases;
                break;
            case feedFilters:
                context = groups;
                view = feedFilters;
                break;
            case retargetings:
                context = groups;
                view = retargetings;
                break;
            case usersProfile:
                context = groups;
                view = usersProfile;
                break;
            default:
                throw new Error(`Для значения ${viewString} не определено действие`);
        }

        return {
            context,
            view,
            login,
            pathname
        };
    }

    static isViewValid(viewString: string): boolean {
        switch (viewString) {
            case campaigns:
            case groups:
            case banners:
            case phrases:
            case retargetings:
            case feedFilters:
            case usersProfile:
            case '':
            case undefined:
                return true;
            default:
                return false;
        }
    }

    static normilizeErrorTypeParam(errorType: string): GridAlertMessageType | undefined {
        switch (errorType) {
            case GridAlertMessageType.minusPhraseBindingError:
                return GridAlertMessageType.minusPhraseBindingError;
            case GridAlertMessageType.minusPhraseUnbindingError:
                return GridAlertMessageType.minusPhraseUnbindingError;
            case GridAlertMessageType.regionsTransformError:
                return GridAlertMessageType.regionsTransformError;
            default:
                return undefined;
        }
    }

    static normalizeSourceParams(
        {
            viewString = campaigns,
            campaignsIds,
            groupsIds,
            goalIds,
            sortKey,
            sortDirection,
            sortGoalId,
            statPreset = '',
            statFrom,
            statTo,
            filter,
            login,
            messageType,
            alertMessageFilter,
            campaignsCalcTypeFilter,
            clientInfo
        }: NormalizeSourceParamsData,
        pathname: string,
        features: ClientFeatures
    ): GridSourceParams {
        const res = this.normalizeSourceParamsWithoutFilters(viewString, login, pathname);
        const { context, view } = res;
        const hasOnlyCpmCampaigns = campaignsCalcTypeFilter === CampaignsCalcTypeFilter.CPM;

        if (context !== clients) {
            res.campaignsIds = campaignsIds;

            if (context !== campaigns) {
                res.groupsIds = groupsIds;
            }
        }

        res.sortParams = this.normalizeSortParams(sortKey, sortDirection, sortGoalId);

        res.campaignsCalcTypeFilter = campaignsCalcTypeFilter;

        const { statPeriod } = Session.getValueFromCurrentSession(login);
        const serializedPeriodFromLocalStorage = serializePeriodFilterForSourceParams(
            preparePeriodFilterAfterLocalStorage(statPeriod)
        );

        if (statFrom && statTo) {
            // TODO валидация строк с датами
            res.statPeriod = {
                statFrom,
                statTo
            };
        } else if (DateRangePreset[statPreset]) {
            res.statPeriod = {
                statPreset: DateRangePreset[statPreset]
            };
        } else if (serializedPeriodFromLocalStorage) {
            res.statPeriod = serializedPeriodFromLocalStorage;
        } else {
            res.statPeriod = {
                statPreset: DateRangePreset.last30Days
            };
        }

        if (filter) {
            res.filter = this.normalizeFilter(view, filter, features, hasOnlyCpmCampaigns, clientInfo);
        }

        if (alertMessageFilter) {
            res.alertMessageFilter = this.normalizeFilter(view, alertMessageFilter, features, hasOnlyCpmCampaigns);
        }

        res.messageType = this.normilizeErrorTypeParam(messageType);

        res.goalIds = goalIds;

        return res;
    }

    static getColumnKeyBySortParams(sortParams: SortParams): string {
        const { key, goalId } = sortParams;

        if (goalId) {
            return `${key}_${goalId}`;
        }

        return key;
    }

    static areParamsMeaningfullyDifferent(
        newParams: GridSourceParams | undefined,
        oldParams: GridSourceParams | undefined,
        features: ClientFeatures,
        hasOnlyCpmCampaigns?: boolean,
        goals?: Record<string, Goal>,
        clientInfo?: ClientInfo
    ): boolean {
        if (newParams === oldParams) {
            return false;
        }

        if (newParams !== undefined && oldParams !== undefined) {
            const isFilterDifferent = GridSourceHelper.isFiltersMeaningfullyDifferent(
                oldParams.filter,
                newParams.filter,
                newParams.view,
                features,
                clientInfo,
                hasOnlyCpmCampaigns
            );

            return (
                isFilterDifferent ||
                !isEqual(omit(newParams, ['filter']), omit(oldParams, ['filter'])) ||
                oldParams.login !== newParams.login
            );
        }

        return true;
    }

    static areParamsMapsToDifferentRowsets(
        newParams: GridSourceParams | undefined,
        oldParams: GridSourceParams | undefined,
        features: ClientFeatures,
        clientInfo?: ClientInfo,
        hasOnlyCpmCampaigns?: boolean
    ): boolean {
        if (newParams === oldParams) {
            return false;
        }
        if (newParams === undefined || oldParams === undefined) {
            return true;
        }

        return (
            ['view', 'campaignsIds', 'groupsIds', 'login', 'additionalFilter'].some(
                key => !isEqual(newParams[key], oldParams[key])
            ) ||
            GridSourceHelper.isFiltersMeaningfullyDifferent(
                oldParams.filter,
                newParams.filter,
                newParams.view,
                features,
                clientInfo,
                hasOnlyCpmCampaigns
            )
        );
    }

    private static normalizeSortParams(
        sortKey?: string,
        sortDirection?: string,
        sortGoalId?: string
    ): SortParams | undefined {
        return UrlHelpers.normalizeSortParams(sortKey, sortDirection, sortGoalId);
    }

    private static normalizeFilter(
        view: GridViewType,
        filterString: string,
        features: ClientFeatures,
        hasOnlyCpmCampaigns?: boolean,
        clientInfo?: ClientInfo
    ): FilterPartialExpression[] {
        const criteria = getAvailableFilterCriteria(view, features, hasOnlyCpmCampaigns, clientInfo);

        return parseFilterFromString(criteria, filterString, true);
    }

    private static isFiltersMeaningfullyDifferent(
        oldFilters: Array<FilterPartialExpression> = [],
        newFilters: Array<FilterPartialExpression> = [],
        view: GridViewType,
        features: ClientFeatures,
        clientInfo?: ClientInfo,
        hasOnlyCpmCampaigns?: boolean
    ): boolean {
        if (oldFilters === newFilters) {
            return false;
        }

        const filterCriteriaContainers = getFilterCriteriaContainersByView(
            view,
            features,
            clientInfo,
            hasOnlyCpmCampaigns
        );

        const filterCriteria = flattenContainer(filterCriteriaContainers);

        const expressions = getValidExpressions(filterCriteria, newFilters);

        const otherExpressions = getValidExpressions(filterCriteria, oldFilters);

        return serializeFilterToString(expressions) !== serializeFilterToString(otherExpressions);
    }
}
